<template>
  <section class="sale-payment-plan-toolbar app-filter-toolbar">
    <div class="toolbar-main-row app-filter-toolbar-row">
      <div class="search-wrap app-filter-search-wrap">
        <i class="fa fa-search" aria-hidden="true"></i>
        <input
          v-model="searchModel"
          type="text"
          :placeholder="text.toolbar.searchPlaceholder"
        />
      </div>

      <div class="toolbar-right app-filter-toolbar-right">
        <AppFilterDateRange
          v-model="dateRangeModel"
          class="app-filter-date-inline"
          label=""
          :show-input="false"
          :presets="dateRangePresets"
          initial-preset-key=""
        />

        <multiselect
          v-if="showCustomerFilter"
          v-model="customerFilterModel"
          :options="customerFilterOptions"
          track-by="value"
          label="label"
          :multiple="false"
          :close-on-select="true"
          :clear-on-select="false"
          :preserve-search="false"
          :show-labels="false"
          :searchable="true"
          :allow-empty="false"
          :max-height="220"
          :placeholder="text.toolbar.customer.placeholder"
          class="sale-payment-plan-customer-inline app-filter-inline-select app-filter-select app-select app-select--filters"
        >
          <template #singleLabel="{ option }">
            <span>{{ option?.label || text.toolbar.customer.allLabel }}</span>
          </template>
          <template #noResult>
            <span>{{ text.toolbar.emptyOption }}</span>
          </template>
          <template #noOptions>
            <span>{{ text.toolbar.emptyOption }}</span>
          </template>
        </multiselect>

        <multiselect
          v-model="billedFilterModel"
          :options="billedFilterOptions"
          track-by="value"
          label="label"
          :multiple="false"
          :close-on-select="true"
          :clear-on-select="false"
          :preserve-search="false"
          :show-labels="false"
          :searchable="false"
          :allow-empty="false"
          :max-height="220"
          :placeholder="text.toolbar.billed.placeholder"
          class="sale-payment-plan-billed-inline app-filter-inline-select app-filter-select app-select app-select--filters"
        >
          <template #singleLabel="{ option }">
            <span>{{ text.toolbar.billed.singleLabelPrefix }} {{ option?.label || text.toolbar.billed.options.all }}</span>
          </template>
          <template #noResult>
            <span>{{ text.toolbar.emptyOption }}</span>
          </template>
          <template #noOptions>
            <span>{{ text.toolbar.emptyOption }}</span>
          </template>
        </multiselect>
      </div>
    </div>
  </section>
</template>

<script>
import AppFilterDateRange from '@/components/common/AppFilterDateRange.vue';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('payments');

export default {
  name: 'SalePaymentPlanToolbar',
  components: {
    AppFilterDateRange,
  },
  props: {
    search: {
      type: String,
      default: '',
    },
    billedFilter: {
      type: String,
      default: 'all',
    },
    dateRangeValue: {
      type: [Array, Object, String, null],
      default: null,
    },
    dateRangePresets: {
      type: Array,
      default: () => [],
    },
    showCustomerFilter: {
      type: Boolean,
      default: false,
    },
    customerFilter: {
      type: [String, Number],
      default: 'all',
    },
    customerOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:search', 'update:billedFilter', 'update:dateRangeValue', 'update:customerFilter'],
  data() {
    return {
      text,
      billedFilterOptions: [
        { value: 'all', label: text.toolbar.billed.options.all },
        { value: 'yes', label: text.toolbar.billed.options.yes },
        { value: 'no', label: text.toolbar.billed.options.no },
      ],
      customerFilterOptions: [
        { value: 'all', label: text.toolbar.customer.allLabel },
      ],
    };
  },
  mounted() {
    if (this.showCustomerFilter) {
      this.syncCustomerFilterOptions();
    }
  },
  watch: {
    showCustomerFilter(nextValue) {
      if (!nextValue) {
        this.customerFilterOptions = [{ value: 'all', label: this.text.toolbar.customer.allLabel }];
        return;
      }

      this.syncCustomerFilterOptions();
    },
    customerOptions: {
      deep: true,
      handler() {
        if (!this.showCustomerFilter) {
          return;
        }

        this.syncCustomerFilterOptions();
      },
    },
  },
  computed: {
    searchModel: {
      get() {
        return String(this.search || '');
      },
      set(value) {
        this.$emit('update:search', String(value || ''));
      },
    },
    billedFilterModel: {
      get() {
        const current = String(this.billedFilter || 'all');
        return this.billedFilterOptions.find((option) => option.value === current)
          || this.billedFilterOptions[0];
      },
      set(value) {
        this.$emit('update:billedFilter', String(value?.value || 'all'));
      },
    },
    dateRangeModel: {
      get() {
        return this.dateRangeValue;
      },
      set(value) {
        this.$emit('update:dateRangeValue', value);
      },
    },
    customerFilterModel: {
      get() {
        const current = String(this.customerFilter || 'all');
        return this.customerFilterOptions.find((option) => String(option?.value || '') === current)
          || this.customerFilterOptions[0];
      },
      set(value) {
        const rawValue = String(value?.value || 'all');
        this.$emit('update:customerFilter', rawValue);
      },
    },
  },
  methods: {
    normalizeCustomerOptions(options = []) {
      const source = Array.isArray(options) ? options : [];
      const normalized = new Map();

      source.forEach((option) => {
        if (!option || typeof option !== 'object') {
          return;
        }

        const value = String(option?.value || '').trim();
        const label = String(option?.label || '').trim();

        if (value === '' || value === 'all' || label === '') {
          return;
        }

        if (!normalized.has(value)) {
          normalized.set(value, { value, label });
        }
      });

      return [
        { value: 'all', label: this.text.toolbar.customer.allLabel },
        ...Array.from(normalized.values()),
      ];
    },
    syncCustomerFilterOptions() {
      this.customerFilterOptions = this.normalizeCustomerOptions(this.customerOptions);

      const currentValue = String(this.customerFilter || 'all').trim() || 'all';
      const hasCurrentValue = this.customerFilterOptions.some((option) => String(option?.value || '') === currentValue);

      if (!hasCurrentValue) {
        this.$emit('update:customerFilter', 'all');
      }
    },
  },
};
</script>

<style scoped>
.sale-payment-plan-toolbar {
  margin: 0;
}

.sale-payment-plan-billed-inline {
  --app-filter-inline-width: 190px;
}

.sale-payment-plan-customer-inline {
  --app-filter-inline-width: 220px;
}

@media (max-width: 767px) {
  .sale-payment-plan-customer-inline,
  .sale-payment-plan-billed-inline {
    width: 100%;
  }
}
</style>
